// SPDX-License-Identifier: AGPL-3.0-or-later

import * as Modal from '@app/features/app/components/dialogs/Modal';
import selectorStyles from '@app/features/app/components/dialogs/shared/SelectorModalStyles.module.css';
import {FriendSelector} from '@app/features/app/components/shared/FriendSelector';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {createHiddenDm, createTempDm} from '@app/features/vault/commands/VaultCommands';
import {Trans} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import {useCallback, useState} from 'react';

export type TempDmCreateKind = 'temp' | 'hidden';

export const TempDmCreateModal = observer(({kind = 'temp'}: {kind?: TempDmCreateKind}) => {
	const [selectedUserIds, setSelectedUserIds] = useState<Array<string>>([]);
	const [searchQuery, setSearchQuery] = useState('');
	const [isCreating, setIsCreating] = useState(false);
	const handleToggle = useCallback((userId: string) => {
		setSelectedUserIds((current) => (current.includes(userId) ? [] : [userId]));
	}, []);
	const handleCreate = useCallback(async () => {
		const userId = selectedUserIds[0];
		if (!userId || isCreating) return;
		setIsCreating(true);
		try {
			if (kind === 'hidden') {
				await createHiddenDm(userId);
			} else {
				await createTempDm(userId);
			}
			ModalCommands.pop();
		} finally {
			setIsCreating(false);
		}
	}, [selectedUserIds, isCreating, kind]);
	return (
		<Modal.Root size="small" centered data-flx="vault.temp-dm-create-modal.modal-root" data-vault-create-kind={kind}>
			<Modal.Header
				title={kind === 'hidden' ? <Trans>New hidden DM</Trans> : <Trans>New temporary DM</Trans>}
				data-flx="vault.temp-dm-create-modal.modal-header"
			/>
			<Modal.Content className={selectorStyles.selectorContent} data-flx="vault.temp-dm-create-modal.modal-content">
				<FriendSelector
					selectedUserIds={selectedUserIds}
					onToggle={handleToggle}
					maxSelections={1}
					searchQuery={searchQuery}
					onSearchQueryChange={setSearchQuery}
					showSearchInput={false}
					data-flx="vault.temp-dm-create-modal.friend-selector"
				/>
			</Modal.Content>
			<Modal.Footer className={selectorStyles.footer} data-flx="vault.temp-dm-create-modal.modal-footer">
				<Button
					variant="secondary"
					onClick={() => ModalCommands.pop()}
					data-flx="vault.temp-dm-create-modal.button.pop"
				>
					<Trans>Cancel</Trans>
				</Button>
				<Button
					onClick={handleCreate}
					disabled={selectedUserIds.length === 0 || isCreating}
					submitting={isCreating}
					data-flx="vault.temp-dm-create-modal.button.create"
				>
					<Trans>Create</Trans>
				</Button>
			</Modal.Footer>
		</Modal.Root>
	);
});
